import React, { useState, useCallback, useEffect } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { Send, FileCheck, Truck, HelpCircle, FileEdit, Stamp, Clock, AlarmClock, Hourglass } from 'lucide-react';
import { dynamicApi } from '../../services/api';
import { useDbNavigate } from '../../context/DatabaseContext';
import DashboardLayout from '../../components/common/DashboardLayout';
import DashboardTile from '../../components/common/DashboardTile';
import { PieTooltip } from '../../components/common/ChartTooltips';

/**
 * TransmittalsDashboardPage - the PM > Transmittals landing page.
 *
 * One tile per log (Submittal, Material Transfer, RFI), laid out on the Engineering Dashboard's
 * pattern, each clicking through to its log. Every tile carries the same four statistics:
 *   - total, plus a count per TransmittalStatus whose Applies_To matches the type;
 *   - draft vs published (the Published stamp is NULL on a draft);
 *   - aging: the average Aging of open transmittals and how many exceed AGING_THRESHOLD_DAYS.
 *     "Open" means no Response_Date yet, since that is the only closure the log records for
 *     every type;
 *   - awaiting response: sent (Transfer_Date set) but no Response_Date.
 *
 * The figures are computed client-side from the dynamic API — the same Transmittal_Log rows the
 * logs list (filtered by Transmittal_Type) and the same TransmittalStatus rows their pickers use.
 */

const LOGS = [
  { key: 'Submittal',         label: 'Submittal Log',         slug: 'submittal',         icon: FileCheck },
  { key: 'Material Transfer', label: 'Material Transfer Log', slug: 'material-transfer', icon: Truck },
  { key: 'RFI',               label: 'RFI Log',               slug: 'rfi',               icon: HelpCircle },
];

const AGING_THRESHOLD_DAYS = 14;

// Statuses are data, not a fixed list, so each is coloured by its display position. Hex for the
// Recharts cell fill, paired with the matching stui-c-* class for the legend dot — the same
// palette the Engineering Dashboard cycles for high-cardinality fields.
const PALETTE = [
  ['#3b82f6', 'stui-c-blue'], ['#22c55e', 'stui-c-green'], ['#f59e0b', 'stui-c-amber'],
  ['#ef4444', 'stui-c-red'], ['#8b5cf6', 'stui-c-violet'], ['#06b6d4', 'stui-c-cyan'],
  ['#f97316', 'stui-c-orange'], ['#10b981', 'stui-c-emerald-light'], ['#6366f1', 'stui-c-indigo'],
  ['#ec4899', 'stui-c-pink'], ['#14b8a6', 'stui-c-teal'], ['#84cc16', 'stui-c-lime'],
  ['#a855f7', 'stui-c-purple'], ['#0ea5e9', 'stui-c-sky'], ['#d946ef', 'stui-c-fuchsia'],
];
const NO_STATUS = ['#94a3b8', 'stui-c-slate'];

const StatCard = ({ value, label, icon: Icon, colorClass = 'stui-c-primary' }) => (
  <div className="stui-dashboard-visual-stat-card">
    <div className={`stui-dashboard-visual-stat-icon ${colorClass}`}>
      <Icon size={24} />
    </div>
    <div className="stui-dashboard-visual-stat-value">{value}</div>
    <div className="stui-dashboard-visual-stat-label">{label}</div>
  </div>
);

const StatusDonut = ({ data, total }) => (
  <div className="stui-dashboard-visual-section">
    <h4>By Status</h4>
    {data.length > 0 ? (
      <ResponsiveContainer width={90} height={90}>
        <PieChart>
          <Pie data={data} dataKey="value" cx="50%" cy="50%" innerRadius={24} outerRadius={40}
            paddingAngle={1} animationDuration={600}>
            {data.map((entry, i) => <Cell key={i} fill={entry.fill} stroke="none" />)}
          </Pie>
          <Tooltip content={<PieTooltip />} />
          <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central"
            className="stui-dashboard-visual-donut-label-sm">
            {total.toLocaleString()}
          </text>
        </PieChart>
      </ResponsiveContainer>
    ) : (
      <div className="stui-dashboard-visual-donut stui-empty stui-sm">
        <span className="stui-dashboard-visual-donut-center">0</span>
      </div>
    )}
  </div>
);

const hasValue = v => v !== null && v !== undefined && v !== '';

function summarise(rows, statuses) {
  const byStatus = new Map(statuses.map((s, i) => [s.id, { status: s, color: PALETTE[i % PALETTE.length], count: 0 }]));
  let noStatus = 0;
  let published = 0;
  let awaiting = 0;
  let openCount = 0;
  let agingSum = 0;
  let agingN = 0;
  let overdue = 0;

  for (const r of rows) {
    const bucket = byStatus.get(r.transmittalStatus);
    if (bucket) bucket.count += 1; else noStatus += 1;
    if (hasValue(r.published)) published += 1;
    const open = !hasValue(r.response_Date);
    if (open && hasValue(r.transfer_Date)) awaiting += 1;
    if (open) {
      openCount += 1;
      const days = Number(r.aging);
      if (hasValue(r.aging) && Number.isFinite(days)) {
        agingSum += days;
        agingN += 1;
        if (days > AGING_THRESHOLD_DAYS) overdue += 1;
      }
    }
  }

  // Every applicable status is listed, zeros included, so the legend reads the same from one
  // project to the next; the donut shows only the non-zero slices.
  const breakdown = [...byStatus.values()].map(b => ({
    name: b.status.status_Name, value: b.count, fill: b.color[0], colorClass: b.color[1],
  }));
  if (noStatus > 0) {
    breakdown.push({ name: 'No Status', value: noStatus, fill: NO_STATUS[0], colorClass: NO_STATUS[1] });
  }

  return {
    total: rows.length,
    published,
    draft: rows.length - published,
    awaiting,
    openCount,
    avgAging: agingN > 0 ? Math.round(agingSum / agingN) : 0,
    overdue,
    breakdown,
  };
}

const TransmittalsDashboardPage = () => {
  const navigate = useDbNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({});

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [st, ...logs] = await Promise.all([
        dynamicApi.getAll('TransmittalStatus'),
        ...LOGS.map(l => dynamicApi.getFilteredEncoded('Transmittal_Log', 'Transmittal_Type', l.key)),
      ]);
      const allStatuses = st.data || [];
      const next = {};
      LOGS.forEach((l, i) => {
        const statuses = allStatuses
          .filter(s => s.applies_To === l.key && s.is_Active !== false)
          .sort((a, b) => (a.display_Order ?? 0) - (b.display_Order ?? 0));
        next[l.key] = summarise(logs[i].data || [], statuses);
      });
      setStats(next);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to load transmittals');
    } finally {
      setLoading(false);
    }
  }, []);

  // Transmittals belong to one project database; re-read when the selection changes.
  useEffect(() => {
    const handler = () => load();
    window.addEventListener('pmd-database-changed', handler);
    return () => window.removeEventListener('pmd-database-changed', handler);
  }, [load]);

  return (
    <DashboardLayout
      id="transmittals"
      title="Transmittals"
      icon={Send}
      onRefresh={load}
      loading={loading}
      error={error}
    >
      {LOGS.map(l => {
        const s = stats[l.key];
        if (!s) return null;
        return (
          <DashboardTile
            key={l.key}
            title={l.label}
            icon={l.icon}
            onClick={() => navigate(`/transmittals/${l.slug}`)}
            legend={s.breakdown.map(b => ({ label: b.name, value: b.value.toLocaleString(), colorClass: b.colorClass }))}
          >
            <div className="stui-dashboard-visual-section-content">
              <div className="stui-dashboard-visual-stat-row">
                <div className="stui-dashboard-visual-total">
                  <span className="stui-dashboard-visual-total-value">{s.total.toLocaleString()}</span>
                  <span className="stui-dashboard-visual-total-label">Total</span>
                </div>
                <StatusDonut data={s.breakdown.filter(b => b.value > 0)} total={s.total} />
              </div>
              <div className="stui-dashboard-visual-stat-row">
                <StatCard value={s.draft} label="Draft" icon={FileEdit} colorClass="stui-c-amber" />
                <StatCard value={s.published} label="Published" icon={Stamp} colorClass="stui-c-green" />
                <StatCard value={s.awaiting} label="Awaiting Response" icon={Hourglass} colorClass="stui-c-blue" />
              </div>
              <div className="stui-dashboard-visual-stat-row">
                <StatCard value={s.openCount} label="Open" icon={Clock} colorClass="stui-c-indigo" />
                <StatCard value={s.avgAging} label="Avg Days Open" icon={Clock} colorClass="stui-c-violet" />
                <StatCard value={s.overdue} label={`Over ${AGING_THRESHOLD_DAYS} Days`} icon={AlarmClock} colorClass="stui-c-red" />
              </div>
            </div>
          </DashboardTile>
        );
      })}
    </DashboardLayout>
  );
};

export default TransmittalsDashboardPage;
